import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { formatRoute } from 'react-router-named-routes';
// material-ui/ core components
import extendedTablesStyle from 'assets/jss/material-dashboard-pro-react/views/extendedTablesStyle';
import withStyles from '@material-ui/core/styles/withStyles';
// material-ui icons
import Edit from '@material-ui/icons/Edit';
import Add from '@material-ui/icons/Add';
// core components
import GridItem from 'components/UI/Grid/GridItem';
import Button from 'components/UI/CustomButtons/Button';
import GridContainer from 'components/UI/Grid/GridContainer';
// global variables
import { processorRulesStatus } from 'config';
// our components
import { PROCESSOR_RULE } from 'routes/paths';
import RouterContext from 'helpers/routerContext';
import printParams from 'helpers/showProcessorRuleParamsHelper';
import WithPermission from 'components/User/WithPermission';
import dateFormat from 'helpers/dateHelper';

/**
 * @description Normalize trigger fields label for rendering.
 * @param {Array|string} triggerFields - Trigger fields as array or JSON string.
 * @returns {string} - Formatted trigger fields label.
 */
const getTriggerFieldsLabel = triggerFields => {
  if (Array.isArray(triggerFields)) {
    return triggerFields.length > 0 ? triggerFields.join(', ') : '[]';
  }

  if (typeof triggerFields === 'string' && triggerFields.trim() !== '') {
    try {
      const parsedTriggerFields = JSON.parse(triggerFields);
      const hasTriggerFields =
        Array.isArray(parsedTriggerFields) && parsedTriggerFields.length > 0;
      return hasTriggerFields ? parsedTriggerFields.join(', ') : '[]';
    } catch (error) {
      return triggerFields;
    }
  }

  return '[]';
};

class ProcessorRuleDetail extends React.Component {
  static contextType = RouterContext;

  /**
   * @description
   * Redirect to processor_rule/create route.
   */
  handleCreate = () => {
    const history = this.context;
    const { routePaths } = this.props;
    history.push(routePaths.CREATE);
  };

  /**
   * @description
   * Redirect to processor_rule/update route.
   */
  handleUpdate = () => {
    const history = this.context;
    const { data, routePaths } = this.props;
    history.push(formatRoute(routePaths.UPDATE, { id: data.id }));
  };

  render() {
    const { classes, t, data } = this.props;
    return (
      <div className={classes.pageContainer}>
        <GridContainer>
          <GridItem xs={12} sm={6}>
            <h3 className={classes.title}>{data && data.name}</h3>
          </GridItem>
          <GridItem xs={12} sm={6}>
            <GridContainer justify="flex-end">
              <GridItem>
                {data.status !== processorRulesStatus.DELETED && (
                  <WithPermission allowPermissions={['change_processorrule']}>
                    <Button
                      color="primary"
                      onClick={this.handleUpdate}
                      justIcon
                      id="processorRule-edit"
                    >
                      <Edit />
                    </Button>
                  </WithPermission>
                )}
                <WithPermission allowPermissions={['add_processorrule']}>
                  <Button
                    color="success"
                    justIcon
                    onClick={this.handleCreate}
                    id="processorRule-create"
                  >
                    <Add />
                  </Button>
                </WithPermission>
              </GridItem>
            </GridContainer>
          </GridItem>
        </GridContainer>

        <div className={classes.contentContainer}>
          <GridContainer>
            {data && data.createdBy && (
              <GridItem xs={12} sm={6}>
                <h6>{t('processor_rule.details.createdBy')}</h6>
                <p>
                  {data.createdBy.fullname} - {dateFormat(data.createdOn, t)}
                </p>
              </GridItem>
            )}
            {data && data.updatedBy && (
              <GridItem xs={12} sm={6}>
                <h6>{t('processor_rule.details.updatedBy')}</h6>
                <p>
                  {data.updatedBy.fullname} - {dateFormat(data.updatedOn, t)}
                </p>
              </GridItem>
            )}
          </GridContainer>
          <GridContainer>
            <GridItem xs={12} sm={6}>
              <h6>{t('processor_rule.details.group')}</h6>
              <p>{data && data.group}</p>
            </GridItem>
            <GridItem xs={12} sm={6}>
              <h6>{t('processor_rule.details.status')}</h6>
              <p>{data && data.status}</p>
            </GridItem>
          </GridContainer>
          <GridContainer>
            <GridItem xs={12} sm={6}>
              <h6>{t('processor_rule.details.actions')}</h6>
              <p>{data && data.actions && data.actions.join(', ')}</p>
            </GridItem>
            <GridItem xs={12} sm={6}>
              <h6>{t('processor_rule.details.ruleType')}</h6>
              <p>{data && data.ruleType}</p>
            </GridItem>
          </GridContainer>
          <GridContainer>
            <GridItem xs={12}>
              <h6>{t('processor_rule.details.trigger_fields')}</h6>
              <p>{getTriggerFieldsLabel(data && data.triggerFields)}</p>
            </GridItem>
          </GridContainer>
          {data && data.params[0].values.length > 0 && (
            <GridContainer>
              <GridItem xs={12}>
                <h6>{t('processor_rule.details.params')}</h6>
                {data.params && printParams(data, classes)}
              </GridItem>
            </GridContainer>
          )}
        </div>
      </div>
    );
  }
}

/**
 * ProcessorRuleDetail PropTypes
 * @param data - processor rule
 * @param classes - styles
 * @param t - prop from HOC with string translations
 */
ProcessorRuleDetail.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  t: PropTypes.func.isRequired,
  data: PropTypes.instanceOf(Object),
  routePaths: PropTypes.shape({
    CREATE: PropTypes.string.isRequired,
    UPDATE: PropTypes.string.isRequired,
  }),
};

ProcessorRuleDetail.defaultProps = {
  data: undefined,
  routePaths: PROCESSOR_RULE,
};

// to fix: The withStyles HOC set contextTypes to a children component
const ProcessorRuleDeleteWrapper = props => <ProcessorRuleDetail {...props} />;

export default withTranslation()(
  withStyles(extendedTablesStyle)(ProcessorRuleDeleteWrapper),
);
